import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, Phone, Calendar } from 'lucide-react';
import EmailVerification from '../components/EmailVerification';
import api from '../services/api';
import { toast } from 'react-toastify';
import PhoneVerification from '../components/PhoneVerification';

const InputField = ({ id, name, type = 'text', placeholder, value, onChange, required, icon: Icon, rightElement }) => (
  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
    {Icon && (
      <Icon
        size={16}
        style={{
          position: 'absolute',
          left: '14px',
          color: '#94a3b8',
          pointerEvents: 'none',
          flexShrink: 0,
        }}
      />
    )}
    <input
      id={id}
      name={name}
      type={type}
      required={required}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: Icon ? '11px 40px 11px 40px' : '11px 14px',
        paddingRight: rightElement ? '44px' : (Icon ? '40px' : '14px'),
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#1e293b',
        background: '#f8fafc',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        boxSizing: 'border-box',
      }}
      onFocus={e => {
        e.target.style.borderColor = '#3b82f6';
        e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)';
        e.target.style.background = '#ffffff';
      }}
      onBlur={e => {
        e.target.style.borderColor = '#e2e8f0';
        e.target.style.boxShadow = 'none';
        e.target.style.background = '#f8fafc';
      }}
    />
    {rightElement && (
      <div style={{ position: 'absolute', right: '12px' }}>
        {rightElement}
      </div>
    )}
  </div>
);

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [registerData, setRegisterData] = useState({
    name: '', email: '', password: '', confirmPassword: '', birthDate: '', phone: ''
  });

  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/home');
  }, [user, navigate]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(formData.email, formData.password);
    if (result.success) {
      navigate('/home');
    } else if (result.requiresEmailVerification) {
      setRegisteredUser({ email: result.email });
      setShowEmailVerification(true);
    }
    setLoading(false);
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    setLoading(true);
    try {
      const { confirmPassword, ...dataToSend } = registerData;
      const { data } = await api.post('/auth/register', dataToSend);
      toast.success('Conta criada, verifique seu email');
      setRegisteredUser(data.user);
      setShowEmailVerification(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao criar conta');
    }
    setLoading(false);
  };

  const handleEmailVerified = () => {
    setShowEmailVerification(false);
    if (registeredUser?.phone) setShowPhoneVerification(true);
    else navigate('/home');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (isRegister) setRegisterData(prev => ({ ...prev, [name]: value }));
    else setFormData(prev => ({ ...prev, [name]: value }));
  };

  const verificationWrapper = (child) => (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%)',
      padding: '24px',
    }}>
      {child}
    </div>
  );

  if (showEmailVerification) {
    return verificationWrapper(
      <EmailVerification
        onVerified={handleEmailVerified}
        userEmail={registeredUser?.email}
        onBackToLogin={() => { setShowEmailVerification(false); setRegisteredUser(null); }}
      />
    );
  }

  if (showPhoneVerification) {
    return verificationWrapper(
      <PhoneVerification
        onVerified={() => { setShowPhoneVerification(false); navigate('/home'); }}
        onSkip={() => { setShowPhoneVerification(false); navigate('/home'); }}
      />
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: '#f1f5f9',
    }}>
      {/* Painel esquerdo — branding */}
      <div style={{
        display: 'none',
        flex: '1',
        background: 'linear-gradient(160deg, #1d4ed8 0%, #3b82f6 50%, #60a5fa 100%)',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px',
        position: 'relative',
        overflow: 'hidden',
      }}
        className="login-brand-panel"
      >
        {/* Círculos decorativos */}
        <div style={{
          position: 'absolute', width: '320px', height: '320px',
          borderRadius: '50%', background: 'rgba(255,255,255,0.06)',
          top: '-80px', right: '-80px',
        }} />
        <div style={{
          position: 'absolute', width: '200px', height: '200px',
          borderRadius: '50%', background: 'rgba(255,255,255,0.06)',
          bottom: '40px', left: '-60px',
        }} />

        <div style={{ position: 'relative', textAlign: 'center', color: '#ffffff' }}>
          <div style={{
            width: '100px', height: '100px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 28px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            backdropFilter: 'blur(4px)',
          }}>
            <img src="/apas.png" alt="Apascentar" style={{ width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover' }} />
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: '0 0 12px', letterSpacing: '-0.5px' }}>
            Apascentar<span style={{ fontWeight: '300', opacity: 0.7 }}> · Curitiba</span>
          </h1>
          <p style={{ fontSize: '15px', fontStyle: 'italic', opacity: '0.9', lineHeight: '1.7', maxWidth: '280px', margin: '0 auto 8px' }}>
            "Porque onde estiverem dois ou três reunidos em meu nome, ali estou no meio deles."
          </p>
          <p style={{ fontSize: '12px', opacity: '0.65', margin: '0 auto 40px' }}>
            Mateus 18:20
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', textAlign: 'left' }}>
            {[
              'Gestão de membros e secretaria',
              'Controle financeiro e dízimos',
              'Eventos, avisos e comunicados',
            ].map((item) => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span style={{ fontSize: '13px', opacity: '0.9' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Painel direito — formulário */}
      <div style={{
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
        minHeight: '100vh',
        overflowY: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>

          {/* Header mobile (logo visível só no mobile) */}
          <div className="login-mobile-header" style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 4px 20px rgba(59,130,246,0.3)',
            }}>
              <img src="/apas.png" alt="Apascentar" style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover' }} />
            </div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px' }}>
              Apascentar<span style={{ fontWeight: '300', color: '#64748b' }}> · Curitiba</span>
            </h1>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              Sistema de Gestão
            </p>
          </div>

          {/* Card do formulário */}
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '36px 32px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9',
          }}>
            <div style={{ marginBottom: '28px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: '0 0 6px' }}>
                {isRegister ? 'Criar conta' : 'Bem-vindo de volta'}
              </h2>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                {isRegister
                  ? 'Preencha os dados para se cadastrar'
                  : 'Acesse sua área do membro'}
              </p>
            </div>

            {!isRegister ? (
              <form onSubmit={handleLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Email
                  </label>
                  <InputField
                    id="email" name="email" type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required icon={Mail}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Senha
                  </label>
                  <InputField
                    id="password" name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Sua senha"
                    value={formData.password}
                    onChange={handleInputChange}
                    required icon={Lock}
                    rightElement={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', color: '#94a3b8' }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    }
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Link
                    to="/forgot-password"
                    style={{ fontSize: '13px', color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}
                    onMouseOver={e => e.target.style.textDecoration = 'underline'}
                    onMouseOut={e => e.target.style.textDecoration = 'none'}
                  >
                    Esqueci minha senha
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: loading ? '#93c5fd' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'opacity 0.2s, transform 0.1s',
                    marginTop: '4px',
                    boxShadow: loading ? 'none' : '0 4px 12px rgba(59,130,246,0.35)',
                  }}
                  onMouseOver={e => { if (!loading) e.currentTarget.style.opacity = '0.92'; }}
                  onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
                >
                  {loading ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}>
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                      </svg>
                      Entrando...
                    </>
                  ) : 'Entrar'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegisterSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Nome Completo</label>
                  <InputField id="name" name="name" placeholder="Seu nome completo" value={registerData.name} onChange={handleInputChange} required icon={User} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Email</label>
                  <InputField id="email" name="email" type="email" placeholder="seu@email.com" value={registerData.email} onChange={handleInputChange} required icon={Mail} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Senha</label>
                    <InputField id="password" name="password" type="password" placeholder="Senha" value={registerData.password} onChange={handleInputChange} required icon={Lock} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Confirmar</label>
                    <InputField id="confirmPassword" name="confirmPassword" type="password" placeholder="Confirme" value={registerData.confirmPassword} onChange={handleInputChange} required icon={Lock} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Data de Nascimento</label>
                  <InputField id="birthDate" name="birthDate" type="date" value={registerData.birthDate} onChange={handleInputChange} required icon={Calendar} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Telefone <span style={{ color: '#94a3b8', fontWeight: '400' }}>(opcional)</span></label>
                  <InputField id="phone" name="phone" type="tel" placeholder="(11) 99999-9999" value={registerData.phone} onChange={handleInputChange} icon={Phone} />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%', padding: '12px',
                    background: loading ? '#93c5fd' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: '#ffffff', border: 'none', borderRadius: '8px',
                    fontSize: '14px', fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    marginTop: '4px',
                    boxShadow: loading ? 'none' : '0 4px 12px rgba(59,130,246,0.35)',
                  }}
                >
                  {loading ? 'Cadastrando...' : 'Criar Conta'}
                </button>
              </form>
            )}

            {/* Divisor */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0 20px' }}>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>ou</span>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
            </div>

            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              style={{
                width: '100%', padding: '11px',
                background: 'transparent',
                color: '#3b82f6',
                border: '1.5px solid #bfdbfe',
                borderRadius: '8px',
                fontSize: '13px', fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s, border-color 0.2s',
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#93c5fd'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
            >
              {isRegister ? 'Já tem uma conta? Faça login' : 'Não tem uma conta? Cadastre-se'}
            </button>
          </div>

          <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '24px' }}>
            © {new Date().getFullYear()} Igreja Apascentar Curitiba
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (min-width: 768px) {
          .login-brand-panel { display: flex !important; }
          .login-mobile-header { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default Login;
